// Chuông khu Quản trị NPP (/npp): hộp thư của tài khoản quản trị trên admin-api (/me/notifications) — tin Super Admin
// gửi xuống. Cùng hình với chuông đơn vị (BellShell); bấm một dòng mở khung đọc đầy đủ.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminGet, adminPost } from '@/lib/adminApi'
import { useNppAdmin } from '@/lib/nppAdmin'
import { BellShell, type BellItem } from '@/components/NotificationBell'
import { toAnnouncement, type AnnouncementContent } from './AnnouncementView'
import { AnnouncementSheet } from './AnnouncementSheet'

const PAGE = 10
const MAX = 50 // máy chủ nhận limit ≤ 50

type Inbox = { notifications: BellItem[]; total: number; unread: number; page: number; limit: number }

export function NppNotificationBell() {
  const admin = useNppAdmin()
  const ready = admin.status === 'ready'
  const [open, setOpen] = useState(false)
  const [limit, setLimit] = useState(PAGE)
  const [reading, setReading] = useState<AnnouncementContent | null>(null)
  const qc = useQueryClient()

  const count = useQuery({
    queryKey: ['npp-inbox', 'count'],
    queryFn: () => adminGet<{ count: number }>('/me/notifications/unread-count').catch(() => ({ count: 0 })),
    enabled: ready,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  const list = useQuery({
    queryKey: ['npp-inbox', 'list', limit],
    queryFn: () => adminGet<Inbox>(`/me/notifications?limit=${limit}`),
    enabled: ready && open,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

  const refresh = () => void qc.invalidateQueries({ queryKey: ['npp-inbox'] })
  const markRead = useMutation({ mutationFn: (id: string) => adminPost(`/me/notifications/${id}/read`), onSuccess: refresh })
  const markAll = useMutation({ mutationFn: () => adminPost('/me/notifications/read-all'), onSuccess: refresh })

  const items = list.data?.notifications
  const more = !!list.data && (items?.length ?? 0) < list.data.total && limit < MAX

  return (
    <>
      <BellShell
        open={open}
        onOpenChange={(v) => {
          setOpen(v)
          if (!v) setLimit(PAGE)
        }}
        unreadCount={count.data?.count ?? 0}
        items={items}
        loading={list.isLoading}
        onItemClick={(item) => {
          if (!item.isRead) markRead.mutate(item.id)
          setOpen(false)
          setLimit(PAGE)
          setReading(toAnnouncement(item))
        }}
        onMarkAll={() => markAll.mutate()}
        markAllPending={markAll.isPending}
        footer={
          more ? (
            <button
              type="button"
              onClick={() => setLimit((n) => Math.min(MAX, n + PAGE))}
              disabled={list.isFetching}
              className="inline-flex min-h-11 items-center gap-1 px-3 text-xs font-medium text-accent hover:underline disabled:opacity-50 desktop:min-h-8"
            >
              Xem thêm
            </button>
          ) : undefined
        }
      />
      <AnnouncementSheet a={reading} onClose={() => setReading(null)} />
    </>
  )
}
